import {
  Component,
  Input,
  Output,
  EventEmitter,
  AfterViewInit,
  ViewChild,
  ElementRef,
  OnChanges,
  SimpleChanges,
  OnInit,
} from '@angular/core';

@Component({
  selector: 'app-phone-number-amount-input',
  templateUrl: './phone-number-amount-input.component.html',
  styleUrl: './phone-number-amount-input.component.scss',
})
export class PhoneNumberAmountInputComponent
  implements OnInit, AfterViewInit, OnChanges
{
  @Input() value: number | string = '';
  @Input() disabled: boolean = false;
  @Input() placeholder: string = '0';
  @Input() currencySymbol: string = '$';
  @Input() autoFocus: boolean = true;

  @Output() valueChange = new EventEmitter<number>();
  @Output() digitsChange = new EventEmitter<string>();

  @ViewChild('amountInput') amountInput!: ElementRef<HTMLInputElement>;

  internalValue: string = '';

  ngOnInit(): void {
    // Initialize internal value from input
    this.updateInternalValue();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      this.updateInternalValue();
    }
  }

  ngAfterViewInit(): void {
    // Focus the input if autoFocus is enabled and input is not disabled
    if (this.autoFocus && !this.disabled && this.amountInput) {
      setTimeout(() => {
        this.amountInput.nativeElement.focus();
        // Move cursor to end
        const input = this.amountInput.nativeElement;
        input.setSelectionRange(input.value.length, input.value.length);
      }, 0);
    }
  }

  /**
   * Format amount for display (convert number to digit string)
   */
  private formatAmountForDisplay(amount: number | string): string {
    if (!amount) return '';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount) || numAmount <= 0) return '';
    // Convert to string of digits (no multiplication by 100)
    return Math.round(numAmount).toString();
  }

  /**
   * Update internal value from external value prop
   */
  private updateInternalValue(): void {
    if (this.value) {
      this.internalValue = this.formatAmountForDisplay(this.value);
    } else {
      this.internalValue = '';
    }
  }

  /**
   * Handle input events - append digits like phone number input
   */
  onAmountInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Remove all non-digit characters - keep only digits
    const digitsOnly = value.replace(/\D/g, '');

    // Update internal value
    this.internalValue = digitsOnly;

    // Emit raw digits
    this.digitsChange.emit(digitsOnly);

    // Convert to number and emit
    if (digitsOnly === '') {
      this.valueChange.emit(0);
    } else {
      const numericValue = parseFloat(digitsOnly);
      this.valueChange.emit(numericValue);
    }

    // Keep cursor at end
    setTimeout(() => {
      input.setSelectionRange(input.value.length, input.value.length);
    }, 0);
  }

  /**
   * Handle keydown events - allow only digits and navigation keys
   */
  onAmountKeydown(event: KeyboardEvent): void {
    // Allow: tab, escape, enter
    if (
      [9, 27, 13].indexOf(event.keyCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (event.keyCode === 65 && event.ctrlKey === true) ||
      (event.keyCode === 67 && event.ctrlKey === true) ||
      (event.keyCode === 86 && event.ctrlKey === true) ||
      (event.keyCode === 88 && event.ctrlKey === true) ||
      // Allow: home, end, left, right
      (event.keyCode >= 35 && event.keyCode <= 39)
    ) {
      return;
    }

    // Handle backspace and delete - let them work normally (will remove last digit)
    if (event.keyCode === 8 || event.keyCode === 46) {
      return;
    }

    // Ensure that it is a number and stop the keypress for non-digits
    if (
      (event.shiftKey || event.keyCode < 48 || event.keyCode > 57) &&
      (event.keyCode < 96 || event.keyCode > 105)
    ) {
      event.preventDefault();
    }
  }

  /**
   * Get the numeric value from the current internal value
   */
  getNumericValue(): number {
    if (!this.internalValue || this.internalValue === '') return 0;

    // Value is stored as digit string
    // Convert to number
    const digitsOnly = this.internalValue.toString().replace(/\D/g, '');
    if (digitsOnly === '') return 0;

    // Return as base units (no division by 100)
    return parseFloat(digitsOnly);
  }
}

